import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ErrorPlaceholder } from "../../../components/common";
import type { SurveyStatisticsResponse } from "../types/survey.types";
import SurveyClosedSurveysIcon from "./icons/SurveyClosedSurveysIcon";
import SurveyOpenSurveysIcon from "./icons/SurveyOpenSurveysIcon";
import SurveyPostedThisWeekIcon from "./icons/SurveyPostedThisWeekIcon";
import SurveyTotalSurveysIcon from "./icons/SurveyTotalSurveysIcon";
import { SurveyStatsSkeleton } from "./survey-skeletons";

export type SurveyPageStat = {
  label: string;
  value: string | number;
  icon?: React.ReactNode;
  color?: string;
  description?: string | React.ReactNode;
};

type SurveyPageStatsProps = {
  className?: string;
  data: SurveyStatisticsResponse | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
};

const getGridClassName = (count: number) => {
  if (count === 2) return "grid-cols-1 sm:grid-cols-2";
  if (count === 3) return "grid-cols-1 sm:grid-cols-2 md:grid-cols-3";
  if (count === 5) return "grid-cols-1 sm:grid-cols-2 lg:grid-cols-5";
  if (count === 6) return "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6";
  return "grid-cols-1 sm:grid-cols-2 md:grid-cols-4";
};

const SurveyPageStats: React.FC<SurveyPageStatsProps> = ({ className = "", data, loading, error, onRetry }) => {
  const { t } = useTranslation();

  const stats = useMemo<SurveyPageStat[]>(
    () => [
      {
        label: t("survey.total_surveys"),
        value: data?.total_surveys ?? 0,
        icon: <SurveyTotalSurveysIcon />,
      },
      {
        label: t("survey.open_surveys"),
        value: data?.open_surveys ?? 0,
        icon: <SurveyOpenSurveysIcon />,
      },
      {
        label: t("survey.posted_this_week"),
        value: data?.posted_this_week ?? 0,
        icon: <SurveyPostedThisWeekIcon />,
      },
      {
        label: t("survey.closed_surveys"),
        value: data?.closed_surveys ?? 0,
        icon: <SurveyClosedSurveysIcon />,
      },
    ],
    [data, t],
  );

  if (loading) {
    return <SurveyStatsSkeleton cards={stats.length} />;
  }

  if (error) {
    return (
      <div className={className}>
        <ErrorPlaceholder
          error={error}
          onRetry={onRetry}
        />
      </div>
    );
  }

  return (
    <div className={`mb-4 grid gap-4 ${getGridClassName(stats.length)} ${className}`.trim()}>
      {stats.map((stat, idx) => (
        <div
          key={`${stat.label}-${idx}`}
          className={`relative rounded-lg border border-gray-200 bg-white p-4 dark:border-[#394150] dark:bg-[#212936] ${stat.color || ""}`}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="text-2xl font-bold text-foreground">{stat.value}</div>
              <div className="mb-1 text-sm text-foreground">{stat.label}</div>
              {stat.description ? (
                <div className="mt-1 text-xs text-foreground">{stat.description}</div>
              ) : null}
            </div>

            {stat.icon ? (
              <div className="ml-4 flex h-8 w-8 items-center justify-center rounded-lg bg-[#F2FCFA] dark:bg-emerald-900/30">
                <div className="text-emerald-600 dark:text-emerald-400">{stat.icon}</div>
              </div>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
};

export default SurveyPageStats;
